import {useCallback, useState} from 'react';
import {useApolloClient} from '@apollo/client';
import {
    GET_CUSTOMER_COMPLAINT_PAGE_LIST,
    GET_CUSTOMER_COMPLAINT_PAGE_LIST_FOR_HQ,
    GET_CUSTOMER_COMPLAINT_PAGE_LIST_FOR_WMS,
    GetCustomerComplaintPageListForHqResponse,
    GetCustomerComplaintPageListForWmsResponse,
    GetCustomerComplaintPageListResponse,
    GetCustomerComplaintPageListVariables,
} from '../graphql/queries/getCustomerComplaintList';
import {
    CustomerComplaint,
    CustomerComplaintHqListFilter,
    CustomerComplaintListFilter,
    WorkbenchMode,
} from '../graphql/types/CustomerComplaintType';

export interface UseCustomerComplaintListResult {
    data: CustomerComplaint[];
    loading: boolean;
    totalElements: number;
    executeQuery: (
        filter: CustomerComplaintListFilter | CustomerComplaintHqListFilter,
        page: number,
        size: number,
    ) => Promise<void>;
}

type ListResponse = GetCustomerComplaintPageListResponse
    & GetCustomerComplaintPageListForWmsResponse
    & GetCustomerComplaintPageListForHqResponse;

/**
 * 客诉列表查询 hook，按视角选择接口：
 *   - DSP：共用接口（旧角色鉴权）
 *   - wms：ForWms（资源码 + 仓库越权双重鉴权），filter.warehouseId 必填
 *   - HQ ：ForHq（纯资源码 CS_DSP_APPEAL_SUMMARY），filter 不含 warehouseId，时间范围必填
 */
export function useCustomerComplaintList(mode: WorkbenchMode = 'DSP'): UseCustomerComplaintListResult {
    const client = useApolloClient();
    const [data, setData] = useState<CustomerComplaint[]>([]);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(false);

    const executeQuery = useCallback(
        async (
            filter: CustomerComplaintListFilter | CustomerComplaintHqListFilter,
            page: number,
            size: number,
        ) => {
            const isHq = mode === 'HQ';
            const isWms = mode === 'wms';
            setLoading(true);
            try {
                const {data: resp} = await client.query<ListResponse, GetCustomerComplaintPageListVariables>({
                    query: isHq
                        ? GET_CUSTOMER_COMPLAINT_PAGE_LIST_FOR_HQ
                        : isWms
                            ? GET_CUSTOMER_COMPLAINT_PAGE_LIST_FOR_WMS
                            : GET_CUSTOMER_COMPLAINT_PAGE_LIST,
                    variables: {
                        filter,
                        pageRequest: {page, size},
                    } as GetCustomerComplaintPageListVariables,
                    fetchPolicy: 'no-cache',
                });
                const pageData = isHq
                    ? resp?.getCustomerComplaintPageListForHq
                    : isWms
                        ? resp?.getCustomerComplaintPageListForWms
                        : resp?.getCustomerComplaintPageListByFilters;
                setData(pageData?.content ?? []);
                setTotalElements(pageData?.totalElements ?? 0);
            } finally {
                setLoading(false);
            }
        },
        [client, mode]
    );

    return {data, loading, totalElements, executeQuery};
}
